import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import { MailCheck, Send, AlertTriangle, CheckCircle2, Users, Search, Radio, Check } from "lucide-react";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SEG_LABEL = { never_verified: "Never verified", abandoned: "Started, abandoned", failed: "Failed / needs input" };
const SEG_TONE = {
  never_verified: "text-muted-foreground border-muted-foreground/40",
  abandoned: "text-amber-400 border-amber-400/40",
  failed: "text-blaze border-blaze/40",
};
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); } catch { return "—"; } };

export default function AdminReverifyCampaign() {
  const [seg, setSeg] = useState(null);           // sender/resend status + total outstanding
  const [audience, setAudience] = useState(null);  // { rows, total }
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [reminded, setReminded] = useState(new Set()); // uids reminded this session
  const [busy, setBusy] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const loadSegments = useCallback(() => {
    api.get("/admin/reverify-campaign/segments").then(({ data }) => setSeg(data)).catch(() => {});
  }, []);
  const loadAudience = useCallback(() => {
    setLoadErr(null);
    api.get("/admin/reverify-campaign/audience?sort=activity_desc").then(({ data }) => setAudience(data))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load the audience."));
  }, []);
  const loadCampaigns = useCallback(() => {
    api.get("/admin/reverify-campaigns").then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  }, []);
  useEffect(() => { loadSegments(); loadAudience(); loadCampaigns(); }, [loadSegments, loadAudience, loadCampaigns]);

  const rows = useMemo(() => audience?.rows || [], [audience]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => (r.username || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q)) : rows;
  }, [rows, search]);
  const total = seg?.total ?? rows.length;
  const senderOk = seg ? seg.sender_ok : true;

  const remindOne = async (r) => {
    setBusy(`one-${r.user_id}`);
    try {
      await api.post(`/admin/accounts/${r.user_id}/reverify-nudge`);
      setReminded((s) => new Set(s).add(r.user_id));
      toast.success(`Reminder sent to @${r.username}.`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const remindAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/admin/reverify-campaign/remind-all");
      toast.success(`Reminded ${data.reminded} outstanding member${data.reminded === 1 ? "" : "s"}.`);
      setConfirmAll(false);
      setReminded((s) => { const n = new Set(s); rows.forEach((r) => n.add(r.user_id)); return n; });
      loadCampaigns();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="text-foreground" data-testid="admin-reverify-campaign">
      <div className="flex items-center gap-2">
        <MailCheck className="w-6 h-6 text-[#d3a24e]" strokeWidth={1.6} />
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="reverify-campaign-title">Re-verification</h1>
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2 max-w-2xl">
        Everyone who hasn't verified their ID yet. Remind one at a time, or remind the whole list at once.
        Status is re-checked before every send — verified members are always skipped, three emails maximum, ever.
      </p>
      <Crosshair className="my-5" />

      {/* Sender guard — broadcasts are blocked unless this is the production sender */}
      {seg && (
        <div className={`mb-3 flex items-center gap-2 border px-3 py-2 font-mono text-xs ${senderOk ? "border-teal-500/40 text-teal-300" : "border-blaze/50 text-blaze"}`} data-testid="reverify-sender-status">
          {senderOk ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          Sender: {seg.sender_email} {senderOk ? "— OK to send" : "— sending BLOCKED until this is noreply@opticswap.com"}
        </div>
      )}
      {/* Delivery metrics (delivered / opened / bounced / complained) stay HIDDEN until the Resend
          webhook is verified in production — no half-true numbers on this screen until then. */}
      {seg && !seg.resend_configured && (
        <div className="mb-5 flex items-start gap-2 border border-amber-400/50 text-amber-400 px-3 py-2 font-mono text-[11px]" data-testid="reverify-resend-status">
          <Radio className="w-4 h-4 mt-0.5 shrink-0" />
          Delivery metrics are hidden until <code>RESEND_WEBHOOK_SECRET</code> is verified in production. Sent + verified counts are live regardless.
        </div>
      )}

      {/* Outstanding list */}
      <div className="border border-sniper bg-surface" data-testid="reverify-outstanding">
        <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-sniper">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-olive-bright" />
            <h2 className="font-heading text-lg uppercase tracking-tight">Outstanding verifications</h2>
            <span className="font-mono text-sm text-secondary-foreground" data-testid="reverify-outstanding-count">({total})</span>
          </div>
          <button
            onClick={() => setConfirmAll(true)}
            disabled={busy === "all" || total === 0 || !senderOk}
            data-testid="reverify-remind-all-btn"
            className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors"
          >
            <Send className="w-4 h-4" /> Remind everyone outstanding
          </button>
        </div>

        <div className="p-4">
          <div className="relative mb-3 max-w-md">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="reverify-search"
              placeholder="Search username / email"
              className="w-full bg-input border border-sniper focus:border-[#d3a24e] outline-none pl-8 pr-3 py-2 text-foreground font-mono text-xs"
            />
          </div>

          <div className="border border-sniper divide-y divide-sniper" data-testid="reverify-outstanding-list">
            {loadErr && <LoadFailed reason={loadErr} onRetry={loadAudience} testid="reverify-load-failed" />}
            {!loadErr && filtered.length === 0 && (
              <p className="px-3 py-6 font-mono text-sm text-muted-foreground text-center">
                {rows.length === 0 ? "Everyone is verified — nothing outstanding." : "No members match your search."}
              </p>
            )}
            {!loadErr && filtered.map((r) => {
              const done = reminded.has(r.user_id);
              return (
                <div key={r.user_id} className="flex items-center gap-3 px-3 py-2.5 flex-wrap" data-testid={`reverify-row-${r.username}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-foreground font-mono text-sm truncate">
                      @{r.username}
                      {r.email_undeliverable && <span className="ml-1.5 text-[8px] uppercase text-blaze border border-blaze/40 px-1">bounced</span>}
                    </div>
                    <div className="text-muted-foreground font-mono text-[11px] truncate">{r.email}</div>
                  </div>
                  <span className={`shrink-0 text-[9px] uppercase border px-1.5 py-0.5 font-mono ${SEG_TONE[r.segment] || ""}`}>{SEG_LABEL[r.segment] || r.segment}</span>
                  <div className="shrink-0 font-mono text-[10px] uppercase text-muted-foreground hidden sm:block w-28 text-right">
                    {r.listings}L · {r.orders}O · {fmtDate(r.created_at)}
                  </div>
                  <button
                    onClick={() => remindOne(r)}
                    disabled={busy === `one-${r.user_id}` || done || r.email_undeliverable}
                    data-testid={`reverify-remind-${r.username}`}
                    className={`shrink-0 inline-flex items-center gap-1 font-mono uppercase text-[10px] px-2.5 py-1.5 border transition-colors disabled:opacity-50 ${
                      done ? "border-olive-bright/60 text-olive-bright" : "border-olive/60 text-olive-bright hover:bg-olive/10"
                    }`}
                  >
                    {done ? <><Check className="w-3 h-3" /> Reminded</> : <><Send className="w-3 h-3" /> Remind</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Past reminders — one line each */}
      <h2 className="font-heading text-lg uppercase tracking-tight mt-8 mb-3">Past reminders</h2>
      <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid="reverify-campaign-list">
        {campaigns.length === 0 && <p className="px-4 py-6 font-mono text-sm text-muted-foreground">No reminders sent yet.</p>}
        {campaigns.map((c) => (
          <div key={c.campaign_id} className="flex items-center justify-between gap-3 px-4 py-2.5 flex-wrap" data-testid={`reverify-campaign-${c.campaign_id}`}>
            <div className="font-mono text-xs text-secondary-foreground min-w-0">
              <span className="text-foreground">{c.name}</span>
              <span className="text-muted-foreground"> · {c.audience_count} reminded · {timeAgo(c.created_at)} by @{c.created_by}</span>
            </div>
            <span className={`shrink-0 font-mono text-[10px] uppercase px-1.5 py-0.5 border ${c.status === "active" ? "text-teal-300 border-teal-500/40" : "text-muted-foreground border-sniper"}`}>{c.status}</span>
          </div>
        ))}
      </div>

      {/* Confirm the mass reminder — this actually emails everyone on the list */}
      <Dialog open={confirmAll} onOpenChange={(o) => !o && setConfirmAll(false)}>
        <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="reverify-remind-all-confirm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Send className="w-5 h-5 text-[#d3a24e]" /> Remind everyone outstanding?
            </DialogTitle>
          </DialogHeader>
          <p className="font-body text-sm text-secondary-foreground">
            This emails a re-verification prompt to all <strong className="text-foreground">{total}</strong> outstanding member{total === 1 ? "" : "s"} right now.
            Anyone already verified, undeliverable, or on the do-not-contact list is skipped automatically.
          </p>
          <DialogFooter className="gap-2">
            <button onClick={() => setConfirmAll(false)} data-testid="reverify-remind-all-cancel"
              className="font-mono uppercase text-xs px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            <button onClick={remindAll} disabled={busy === "all"} data-testid="reverify-remind-all-confirm-btn"
              className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
              <Send className="w-4 h-4" /> {busy === "all" ? "Sending…" : `Send ${total} reminder${total === 1 ? "" : "s"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
